import { Controller, useForm } from "react-hook-form";
import { Input, Button } from "@heroui/react";
import { useEffect } from "react";
import { ModalError } from "../modals/modalBuilder";

interface RenterMetaFormProps {
  defaultValues?: Partial<RenterMetaFormData>;
  onSubmit: (data: RenterMetaFormData) => void;
  isEdit?: boolean;
  modalErrors?: ModalError[];
  onDelete?: () => void;
}

export interface RenterMetaFormData {
  renter_name: string;
  address: string;
  ogrn: string;
  inn: number;
  kpp: string;
  bik: string;
  bank_account: string;
  correspondent_account: string;
  bank_name: string;
  phone_number: string;
  email: string;
}

export function RenterMetaForm({
  defaultValues,
  onSubmit,
  isEdit = false,
  modalErrors = [],
  onDelete,
}: RenterMetaFormProps) {
  const form = useForm<RenterMetaFormData>({
    defaultValues,
  });

  useEffect(() => {
    if (modalErrors.length) {
      modalErrors.forEach(({ field, message }) => {
        form.setError(field as keyof RenterMetaFormData, {
          type: "manual",
          message,
        });
      });
    } else {
      form.clearErrors();
    }
  }, [modalErrors, form]);

  useEffect(() => {
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        if (value !== undefined) {
          form.setValue(key as keyof RenterMetaFormData, value as any);
        }
      });
    }
  }, [defaultValues, form]);

  return (
    <form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4 md:w-1/2">
        <Controller
          control={form.control}
          name="renter_name"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              {...field}
              isRequired
              isInvalid={!!form.formState.errors["renter_name"]}
              errorMessage={
                form.formState.errors["renter_name"]?.message?.toString() || ""
              }
              label="Название арендатора"
              variant="bordered"
            />
          )}
        />

        <Controller
          control={form.control}
          name="address"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              {...field}
              isRequired
              isInvalid={!!form.formState.errors["address"]}
              errorMessage={
                form.formState.errors["address"]?.message?.toString() || ""
              }
              label="Юр. адрес арендатора"
              variant="bordered"
            />
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <Controller
            control={form.control}
            name="ogrn"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                isRequired
                isInvalid={!!form.formState.errors["ogrn"]}
                errorMessage={
                  form.formState.errors["ogrn"]?.message?.toString() || ""
                }
                label="ОГРН"
                variant="bordered"
                value={field.value || ""}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />

          <Controller
            control={form.control}
            name="inn"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                isRequired
                isInvalid={!!form.formState.errors["inn"]}
                errorMessage={
                  form.formState.errors["inn"]?.message?.toString() || ""
                }
                label="ИНН"
                variant="bordered"
                type="number"
                value={field.value?.toString()}
                onChange={(e) => field.onChange(Number(e.target.value))}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Controller
            control={form.control}
            name="kpp"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                isRequired
                isInvalid={!!form.formState.errors["kpp"]}
                errorMessage={
                  form.formState.errors["kpp"]?.message?.toString() || ""
                }
                label="КПП"
                variant="bordered"
                value={field.value || ""}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />

          <Controller
            control={form.control}
            name="bik"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                isRequired
                isInvalid={!!form.formState.errors["bik"]}
                errorMessage={
                  form.formState.errors["bik"]?.message?.toString() || ""
                }
                label="БИК"
                variant="bordered"
                value={field.value || ""}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
        </div>

        <Controller
          control={form.control}
          name="bank_account"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              {...field}
              isRequired
              isInvalid={!!form.formState.errors["bank_account"]}
              errorMessage={
                form.formState.errors["bank_account"]?.message?.toString() || ""
              }
              label="Счёт в банке"
              variant="bordered"
            />
          )}
        />

        <Controller
          control={form.control}
          name="correspondent_account"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              {...field}
              isRequired
              isInvalid={!!form.formState.errors["correspondent_account"]}
              errorMessage={
                form.formState.errors[
                  "correspondent_account"
                ]?.message?.toString() || ""
              }
              label="Корр. счёт"
              variant="bordered"
            />
          )}
        />

        <Controller
          control={form.control}
          name="bank_name"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              {...field}
              isRequired
              isInvalid={!!form.formState.errors["bank_name"]}
              errorMessage={
                form.formState.errors["bank_name"]?.message?.toString() || ""
              }
              label="Название банка"
              variant="bordered"
            />
          )}
        />

        <Controller
          control={form.control}
          name="phone_number"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              {...field}
              isRequired
              isInvalid={!!form.formState.errors["phone_number"]}
              errorMessage={
                form.formState.errors["phone_number"]?.message?.toString() || ""
              }
              label="Номер телефона"
              variant="bordered"
            />
          )}
        />

        <Controller
          control={form.control}
          name="email"
          rules={{
            required: true,
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Некорректный email адрес",
            },
          }}
          render={({ field }) => (
            <Input
              {...field}
              isRequired
              type="email"
              isInvalid={!!form.formState.errors["email"]}
              errorMessage={
                form.formState.errors["email"]?.message?.toString() || ""
              }
              label="Адрес эл.почты"
              variant="bordered"
            />
          )}
        />

        <div className="flex gap-2 justify-end mt-4">
          {isEdit && onDelete && (
            <Button
              color="danger"
              variant="light"
              onPress={onDelete}
              isDisabled={form.formState.isSubmitting}>
              Удалить
            </Button>
          )}
          <Button
            color="primary"
            type="submit"
            isDisabled={form.formState.isSubmitting}>
            {isEdit ? "Сохранить" : "Далее"}
          </Button>
        </div>
      </div>
    </form>
  );
}
